// /admin/v2/activity — what a student did on one India day (BRIEFS C3.4, SPEC §7 "Activity
// dashboard + full log"): student + day pickers, the day's totals and warning flags, then the
// full event log with a type filter and pages. Totals are recomputed by the server on open.
import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { errText, istToday, v2Activity, v2ActivityDays } from '../../../api/v2admin';
import V2Tabs from './V2Tabs';
import StudentPicker from './StudentPicker';

const addDays = (day, n) => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const niceDay = (day) => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const mins = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);

function Tile({ label, value, sub, testId }) {
  return (
    <div className="card" style={{ padding: '14px 16px' }} data-testid={testId}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Sora', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function whatText(e) {
  const parts = [];
  if (e.chapter) parts.push(e.chapter);
  if (e.point_code) parts.push(e.point_code);
  else if (e.section_code) parts.push(e.section_code);
  if (e.question_id) parts.push(`Question ${e.question_id}`);
  if (e.one_liner_id) parts.push(`One-liner ${e.one_liner_id}`);
  if (e.video_title || e.video_clip_id) parts.push(`Video: ${e.video_title || e.video_clip_id}`);
  if (e.test_id) parts.push(`Test ${e.test_id.slice(0, 8)}`);
  if (e.duration_ms != null) parts.push(`${Math.round(e.duration_ms / 1000)} s`);
  return parts.join(' · ');
}

function Flags({ f }) {
  const items = [];
  if (f.no_activity) items.push(['No activity this day', 'badge-yellow']);
  if (f.guess_count) items.push([`${f.guess_count} very fast answers (guesses)`, 'badge-red']);
  if (f.wrong_no_notes) items.push([`${f.wrong_no_notes} wrong answers without opening the notes`, 'badge-red']);
  if (f.longest_idle_minutes) items.push([`Longest break: ${mins(f.longest_idle_minutes)}`, 'badge-blue']);
  if (!items.length) return <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>No warnings.</span>;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} data-testid="v2act-flags">
      {items.map(([t, c]) => <span key={t} className={`badge ${c}`}>{t}</span>)}
    </div>
  );
}

export default function AdminV2Activity() {
  const [params, setParams] = useSearchParams();
  const student = params.get('student') || '';
  const today = istToday();
  const day = params.get('day') || today;
  const type = params.get('type') || '';
  const page = Number(params.get('page') || 1);
  const order = params.get('order') || 'asc';

  const set = useCallback((changes) => {
    const next = { student, day, type, page: String(page), order, ...changes };
    if (!('page' in changes)) next.page = '1';
    const clean = {};
    Object.entries(next).forEach(([k, v]) => {
      if (v && !(k === 'page' && v === '1') && !(k === 'order' && v === 'asc') && !(k === 'day' && v === today)) clean[k] = v;
    });
    setParams(clean);
  }, [student, day, type, page, order, today, setParams]);

  const actQ = useQuery({
    queryKey: ['v2a', 'activity', student, day, type, page, order],
    queryFn: () => v2Activity({ student, day, type: type || undefined, page, order }),
    enabled: !!student,
    placeholderData: keepPreviousData,
  });
  const daysQ = useQuery({ queryKey: ['v2a', 'activity-days', student], queryFn: () => v2ActivityDays(student), enabled: !!student });
  const a = actQ.data;
  const t = a && a.totals;
  const ev = a && a.events;
  const recentDays = (daysQ.data && daysQ.data.days) || [];

  return (
    <div>
      <V2Tabs group="student" title="Activity" intro="What the student did on one day (India time): study time, questions, one-liners, warnings, and every event the app recorded." />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <StudentPicker value={student} autoPick onChange={(id) => set({ student: id })} testId="v2act-student" />
        <button className="btn btn-ghost btn-sm" onClick={() => set({ day: addDays(day, -1) })} aria-label="Previous day">‹ Day before</button>
        <input type="date" className="form-input" value={day} max={today} data-testid="v2act-day"
          onChange={(e) => e.target.value && set({ day: e.target.value })} style={{ maxWidth: 170 }} />
        <button className="btn btn-ghost btn-sm" disabled={day >= today} onClick={() => set({ day: addDays(day, 1) })} aria-label="Next day">Day after ›</button>
        {day !== today && <button className="btn btn-ghost btn-sm" onClick={() => set({ day: today })}>Today</button>}
      </div>
      {recentDays.length > 0 && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          Days with activity:
          {recentDays.slice(0, 14).map((d) => (
            <button key={d} className="btn btn-ghost btn-sm" onClick={() => set({ day: d })}
              style={{ padding: '3px 8px', fontWeight: d === day ? 700 : 400, color: d === day ? 'var(--primary)' : undefined }}>
              {d.slice(5)}
            </button>
          ))}
        </div>
      )}
      {!student && <div className="card" style={{ padding: 18 }}>Choose a student.</div>}
      {actQ.isError && <div className="card" style={{ padding: 18, color: 'var(--danger)' }}>{errText(actQ.error, 'Could not load the activity.')}</div>}
      {student && actQ.isLoading && <div>Loading…</div>}
      {a && (
        <>
          <h2 style={{ fontSize: 18, margin: '4px 0 10px' }} data-testid="v2act-title">
            {a.student.name} — {niceDay(a.day)}
          </h2>
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', marginBottom: 12 }}>
            <Tile testId="v2act-active" label="Study time (active)" value={mins(t.active_minutes)}
              sub={t.first_ist ? `From ${t.first_ist.slice(0, 5)} to ${t.last_ist.slice(0, 5)}` : 'No events'} />
            <Tile label="Notes" value={mins(t.notes_minutes)} />
            <Tile label="Tests" value={mins(t.test_minutes)} />
            <Tile label="Revision" value={mins(t.revision_minutes)} />
            <Tile label="Videos" value={mins(t.video_minutes)} />
            <Tile testId="v2act-questions" label="Questions" value={t.questions_done}
              sub={t.questions_done ? `${t.questions_correct} right · ${t.questions_wrong} wrong · ${t.accuracy}%` : 'None answered'} />
            <Tile label="One-liners" value={t.one_liners_done}
              sub={t.one_liners_done ? `${t.one_liners_correct} right` : 'None answered'} />
            <Tile label="Streak" value={`${a.streak.current} days`}
              sub={`Best ${a.streak.best} · ${t.streak_day ? 'this day counts' : 'this day does not count'}`} />
          </div>
          <div className="card" style={{ padding: '12px 16px', marginBottom: 18 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Warnings (never shown to the student)</div>
            <Flags f={t.flags} />
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <h2 style={{ fontSize: 18, margin: 0 }}>Event log</h2>
            <select className="form-input" value={type} data-testid="v2act-type" style={{ maxWidth: 260 }}
              onChange={(e) => set({ type: e.target.value })}>
              <option value="">All events ({Object.values(a.type_counts).reduce((x, y) => x + y, 0)})</option>
              {Object.entries(a.type_counts).map(([k, n]) => <option key={k} value={k}>{k} ({n})</option>)}
              {type && !(type in a.type_counts) && <option value={type}>{type} (0)</option>}
            </select>
            <select className="form-input" value={order} style={{ maxWidth: 170 }} onChange={(e) => set({ order: e.target.value })}>
              <option value="asc">Oldest first</option>
              <option value="desc">Newest first</option>
            </select>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }} data-testid="v2act-count">
              {ev.total} events · page {ev.page} of {ev.pages}
            </span>
          </div>
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%' }} data-testid="v2act-log">
              <thead><tr><th>Time (India)</th><th>Event</th><th>What</th><th>Device</th><th>Details</th></tr></thead>
              <tbody>
                {ev.items.map((e) => {
                  const det = Object.keys(e.details || {}).length ? JSON.stringify(e.details) : '';
                  return (
                    <tr key={e.id}>
                      <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{e.time_ist}</td>
                      <td><code>{e.type}</code></td>
                      <td style={{ fontSize: 13 }}>{whatText(e)}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.device_name || ''}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 320, wordBreak: 'break-all' }} title={det}>
                        {det.length > 140 ? `${det.slice(0, 140)}…` : det}
                      </td>
                    </tr>
                  );
                })}
                {!ev.items.length && <tr><td colSpan={5} style={{ color: 'var(--text-muted)' }}>No events on this day.</td></tr>}
              </tbody>
            </table>
          </div>
          {ev.pages > 1 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <button className="btn btn-ghost btn-sm" disabled={ev.page <= 1} onClick={() => set({ page: String(ev.page - 1) })}>‹ Previous</button>
              <span style={{ fontSize: 14 }}>Page {ev.page} of {ev.pages}</span>
              <button className="btn btn-ghost btn-sm" disabled={ev.page >= ev.pages} data-testid="v2act-next"
                onClick={() => set({ page: String(ev.page + 1) })}>Next ›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
